import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { db } from '../lib/firebase';
import {
  collection, query, orderBy, onSnapshot,
  doc, addDoc, updateDoc, deleteDoc, getDocs,
  serverTimestamp, writeBatch,
  arrayUnion, arrayRemove
} from 'firebase/firestore';
import { useAuthStore } from './auth';
import { profileToPreferences } from '../lib/profileToPreferences';
import { applyVoteToProfile, applyBlockSense, applyDishSense, quizCounterOnlyUpdates } from '../lib/profileVoteAlgo';
import { withPropagationMarker } from '../lib/profileDefaults';

/**
 * Store pour la sous-collection `users/{uid}/profiles`.
 *
 * Le store doit être bind() avant utilisation, en passant l'uid du user
 * courant (généralement appelé depuis l'auth store après login).
 *
 * Le unbind() est automatique au logout.
 */
export const useProfilesStore = defineStore('profiles', () => {
  const profiles = ref([]);
  const loading = ref(false);
  const currentUid = ref(null);
  let unsub = null;

  function _stopWatcher() {
    if (unsub) {
      unsub();
      unsub = null;
    }
  }

  async function bind(uid) {
    if (!uid || !db) return;
    if (currentUid.value === uid && unsub) return; // already bound to same user
    _stopWatcher();
    currentUid.value = uid;
    loading.value = true;
    const q = query(
      collection(db, 'users', uid, 'profiles'),
      orderBy('createdAt', 'asc')
    );
    unsub = onSnapshot(
      q,
      (snap) => {
        // d.id en dernier pour qu'il ne soit jamais écrasé par un champ
        // `id: null` parasite stocké dans le document.
        profiles.value = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
        loading.value = false;
      },
      (err) => {
        console.error('[profilesStore] onSnapshot error:', err);
        loading.value = false;
      }
    );
  }

  function unbind() {
    _stopWatcher();
    profiles.value = [];
    currentUid.value = null;
  }

  const defaultProfile = computed(() => {
    if (profiles.value.length === 0) return null;
    return profiles.value.find((p) => p.isDefault) || profiles.value[0];
  });

  const profilesById = computed(() => {
    const m = new Map();
    profiles.value.forEach((p) => m.set(p.id, p));
    return m;
  });

  /**
   * Synchronise les `users.preferences` legacy quand on édite un profil isDefault.
   * Echec silencieux : la sauvegarde du profil reste prioritaire, le legacy
   * suit en best-effort.
   */
  async function _syncLegacyIfDefault(profileData) {
    if (!profileData?.isDefault) return;
    try {
      const authStore = useAuthStore();
      if (!authStore.user?.uid) return;
      const legacyPrefs = profileToPreferences(profileData);
      if (legacyPrefs) {
        await authStore.updateProfile({ preferences: legacyPrefs });
      }
    } catch (e) {
      console.warn('[profilesStore] legacy sync failed (silent):', e?.message);
    }
  }

  // Retire les champs réservés (id, timestamps) qui pourraient surcharger
  // le doc Firestore et provoquer un id parasite côté client.
  function _stripReserved(payload) {
    if (!payload) return {};
    const { id, createdAt, updatedAt, ...clean } = payload;
    return clean;
  }

  // Compare deux noms en ignorant casse et espaces. Renvoie true si identiques.
  function _sameName(a, b) {
    return (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();
  }

  async function createProfile(payload) {
    if (!db || !currentUid.value) throw new Error('No user bound');
    // Marqueur propagationFree : ce client ne propage plus les votes de plats aux
    // ingrédients (cf. withPropagationMarker, trigger onProfileCreatedTastes).
    const clean = withPropagationMarker(_stripReserved(payload));

    // Validation : prenom dupliqué (insensible casse/espaces)
    const incomingName = (clean.name || '').trim();
    if (incomingName.length === 0) {
      throw new Error('Le prénom est obligatoire.');
    }
    if (profiles.value.some((p) => _sameName(p.name, incomingName))) {
      throw new Error('Un profil avec ce prénom existe déjà.');
    }

    const colRef = collection(db, 'users', currentUid.value, 'profiles');

    // Si c'est le premier profil ET que personne d'autre n'est défaut, marque-le défaut
    const willBeDefault = clean.isDefault === true || profiles.value.length === 0;

    // Si on flag celui-ci default, retirer le default des autres en batch
    if (willBeDefault) {
      const batch = writeBatch(db);
      const ref = doc(colRef);
      batch.set(ref, {
        ...clean,
        isDefault: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      profiles.value
        .filter((p) => p.isDefault)
        .forEach((p) => {
          batch.update(doc(db, 'users', currentUid.value, 'profiles', p.id), {
            isDefault: false,
            updatedAt: serverTimestamp()
          });
        });
      await batch.commit();
      await _syncLegacyIfDefault({ ...clean, isDefault: true });
      return ref.id;
    }

    const ref = await addDoc(colRef, {
      ...clean,
      isDefault: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    await _syncLegacyIfDefault({ ...clean, isDefault: false });
    return ref.id;
  }

  async function updateProfile(profileId, patch) {
    if (!db || !currentUid.value) throw new Error('No user bound');
    const clean = _stripReserved(patch);

    // Validation : si on renomme, vérifier qu'aucun autre profil n'a déjà ce prénom
    if (Object.prototype.hasOwnProperty.call(clean, 'name')) {
      const incomingName = (clean.name || '').trim();
      if (incomingName.length === 0) {
        throw new Error('Le prénom est obligatoire.');
      }
      if (profiles.value.some((p) => p.id !== profileId && _sameName(p.name, incomingName))) {
        throw new Error('Un profil avec ce prénom existe déjà.');
      }
    }

    // Si on set isDefault: true, retirer default des autres
    if (clean.isDefault === true) {
      const batch = writeBatch(db);
      batch.update(doc(db, 'users', currentUid.value, 'profiles', profileId), {
        ...clean,
        updatedAt: serverTimestamp()
      });
      profiles.value
        .filter((p) => p.isDefault && p.id !== profileId)
        .forEach((p) => {
          batch.update(doc(db, 'users', currentUid.value, 'profiles', p.id), {
            isDefault: false,
            updatedAt: serverTimestamp()
          });
        });
      await batch.commit();
      const updatedProfile = { ...(profiles.value.find(p => p.id === profileId) || {}), ...clean };
      await _syncLegacyIfDefault(updatedProfile);
      return;
    }

    await updateDoc(doc(db, 'users', currentUid.value, 'profiles', profileId), {
      ...clean,
      updatedAt: serverTimestamp()
    });
    const current = profiles.value.find(p => p.id === profileId);
    if (current?.isDefault) {
      await _syncLegacyIfDefault({ ...current, ...clean });
    }
  }

  async function deleteProfile(profileId) {
    if (!db || !currentUid.value) throw new Error('No user bound');
    if (typeof profileId !== 'string' || profileId.length === 0) {
      throw new Error('Identifiant de profil invalide.');
    }
    if (profiles.value.length <= 1) {
      throw new Error('Impossible de supprimer le dernier profil.');
    }
    const target = profiles.value.find((p) => p.id === profileId);
    await deleteDoc(doc(db, 'users', currentUid.value, 'profiles', profileId));

    // Si on a supprimé le défaut, en flag un autre (s'il a un id valide)
    if (target?.isDefault) {
      const remaining = profiles.value.filter((p) => p.id && p.id !== profileId);
      const next = remaining[0];
      if (next?.id) {
        await updateDoc(
          doc(db, 'users', currentUid.value, 'profiles', next.id),
          { isDefault: true, updatedAt: serverTimestamp() }
        );
      }
    }
  }

  /**
   * Plan #11 : enregistre un vote du profil sur un plat (scores ponderes).
   * vote = 'like' | 'dislike' | null (neutre, n'ecrit que le compteur)
   * dishBlockIds = ids des blocs presents dans le plat (pour inference ±1).
   */
  async function voteOnDish(profileId, dishId, vote, dishBlockIds = []) {
    if (!db || !currentUid.value || !profileId || !dishId) {
      throw new Error('Invalid voteOnDish args');
    }
    const ref = doc(db, 'users', currentUid.value, 'profiles', profileId);
    let updates;
    if (vote == null) {
      updates = quizCounterOnlyUpdates();
    } else {
      updates = applyVoteToProfile({ kind: 'dish', itemId: dishId, vote, dishBlockIds });
    }
    await updateDoc(ref, updates);
  }

  /**
   * Plan #11 : enregistre un vote du profil sur un bloc (scores ponderes).
   * vote = 'like' | 'dislike' | null (neutre, n'ecrit que le compteur)
   * options.isDrillDown = true si vote depuis le contexte drill-down (poids ±2).
   */
  async function voteOnBlock(profileId, blockId, vote, options = {}) {
    if (!db || !currentUid.value || !profileId || !blockId) {
      throw new Error('Invalid voteOnBlock args');
    }
    const ref = doc(db, 'users', currentUid.value, 'profiles', profileId);
    let updates;
    if (vote == null) {
      updates = quizCounterOnlyUpdates();
    } else {
      updates = applyVoteToProfile({ kind: 'block', itemId: blockId, vote, isDrillDown: options.isDrillDown });
    }
    await updateDoc(ref, updates);
  }

  /**
   * Corrections manuelles depuis l'editeur de gouts. Pas un vote de quiz :
   * contrairement a voteOnBlock/voteOnDish, votesCount n'est pas incremente.
   */
  async function setBlockSense(profileId, blockId, sense) {
    if (!db || !currentUid.value || !profileId || !blockId) {
      throw new Error('Invalid setBlockSense args');
    }
    const ref = doc(db, 'users', currentUid.value, 'profiles', profileId);
    await updateDoc(ref, applyBlockSense(blockId, sense));
  }

  async function setDishSense(profileId, dishId, sense) {
    if (!db || !currentUid.value || !profileId || !dishId) {
      throw new Error('Invalid setDishSense args');
    }
    const ref = doc(db, 'users', currentUid.value, 'profiles', profileId);
    await updateDoc(ref, applyDishSense(dishId, sense));
  }

  /**
   * Retourne true si le user n'a actuellement aucun profil.
   * Lecture one-shot (n'attend pas le watcher onSnapshot).
   */
  async function hasNoProfile(uid) {
    if (!db || !uid) return false;
    const snap = await getDocs(collection(db, 'users', uid, 'profiles'));
    return snap.empty;
  }

  return {
    profiles,
    profilesById,
    defaultProfile,
    loading,
    currentUid,
    bind,
    unbind,
    createProfile,
    updateProfile,
    deleteProfile,
    hasNoProfile,
    voteOnDish,
    voteOnBlock,
    setBlockSense,
    setDishSense
  };
});
